import { useMemo, useState } from "react";
import { Button } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { setAuthToken } from "../utils/tokenHeaders";
import { getApiBaseUrl, readApiJson } from "../utils/apiBase";
import { useDispatch, useSelector } from "react-redux";
import { getProfile } from "../redux/asyncThunk/userActions";
import AppPageShell from "./AppPageShell";
import { PRICING_FAQ, PRICING_SECTION, MEDIA_STORAGE_SECTION, PRICING_TIER_COMPARISON } from "../content/pricing";
import {
  formatSubscriptionExpiry,
  formatStorageStatusLabel,
  getStorageUsagePercent,
  getAuthUserIdFromState,
  formatBusinessPlanLabel,
  getTrialStatusText,
  isTrialSubscription,
} from "../utils/userProfile";

const url = getApiBaseUrl();

function formatPrice(value) {
  return new Intl.NumberFormat("ru-RU").format(value);
}

function PricingTierComparisonTable({
  columns,
  rows,
  plans,
  selectedPackageId,
  onSelectPlan,
}) {
  const planById = useMemo(
    () => Object.fromEntries(plans.map((plan) => [plan.packageId, plan])),
    [plans],
  );

  const columnClass = (column) => {
    const isSelected = selectedPackageId === column.packageId;
    const classes = [];
    if (column.highlight && !isSelected) {
      classes.push("app-price-tier-table__col--featured");
    }
    if (isSelected) {
      classes.push("app-price-tier-table__col--selected");
    }
    return classes.join(" ");
  };

  return (
    <div className="app-price-tier-table-wrap">
      <table className="app-price-tier-table">
        <thead>
          <tr>
            <th scope="col" className="app-price-tier-table__corner">
              Возможность
            </th>
            {columns.map((column) => {
              const plan = planById[column.packageId];
              const isSelected = selectedPackageId === column.packageId;
              return (
                <th
                  key={column.id}
                  scope="col"
                  className={columnClass(column)}
                  aria-selected={isSelected}
                >
                  <div className="app-price-tier-table__plan-head">
                    {isSelected ? (
                      <span className="app-price-tier-table__selected-badge">
                        Выбран
                      </span>
                    ) : plan?.highlight ? (
                      <span className="app-price-tier-table__popular">Популярный</span>
                    ) : null}
                    <span className="app-price-tier-table__tier">{column.label}</span>
                    {plan ? (
                      <>
                        <span className="app-price-tier-table__plan-name">
                          {plan.title}
                        </span>
                        <span className="app-price-tier-table__plan-price">
                          {formatPrice(plan.price)} &#8381;
                          {plan.pricePeriod ? (
                            <span className="app-price-tier-table__plan-period">
                              {plan.pricePeriod}
                            </span>
                          ) : null}
                        </span>
                      </>
                    ) : null}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.label}>
              <th scope="row">{row.label}</th>
              {row.values.map((value, index) => (
                <td
                  key={`${row.label}-${columns[index].id}`}
                  className={columnClass(columns[index])}
                >
                  {value === "check" ? (
                    <span className="app-price-tier-table__check" aria-label="Включено">
                      ✓
                    </span>
                  ) : (
                    value
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td />
            {columns.map((column) => {
              const selected = selectedPackageId === column.packageId;
              return (
                <td key={`cta-${column.id}`} className={columnClass(column)}>
                  <Button
                    size="small"
                    variant={selected ? "contained" : "outlined"}
                    className={`app-btn app-price-tier-table__cta${
                      selected ? " app-btn--primary app-price-tier-table__cta--selected" : ""
                    }`}
                    onClick={() => onSelectPlan(column.packageId)}
                    aria-pressed={selected}
                  >
                    {selected ? "✓ Выбрано" : "Выбрать"}
                  </Button>
                </td>
              );
            })}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function PriceCheckoutSummary({
  items,
  total,
  loading,
  syncing,
  canPay,
  error,
  info,
  paymentPending,
  onPay,
  onSyncPayment,
}) {
  return (
    <section className="app-price-checkout" aria-live="polite">
      {canPay ? (
        <>
          <ul className="app-price-checkout__items">
            {items.map((item) => (
              <li key={item.packageId} className="app-price-checkout__item">
                <span>{item.title}</span>
                <span>{formatPrice(item.price)} &#8381;</span>
              </li>
            ))}
          </ul>
          {error ? <div className="app-price-error app-price-error--checkout">{error}</div> : null}
          {info ? <div className="app-price-info app-price-info--checkout">{info}</div> : null}
          {paymentPending ? (
            <p className="app-price-checkout__pending">
              После оплаты в ЮMoney вернитесь сюда и нажмите «Проверить оплату» — подписка
              активируется даже если страница банка зависла после 3-D Secure.
            </p>
          ) : null}
          <div className="app-price-checkout__footer">
            <div className="app-price-checkout__total">
              <span>Итого</span>
              <strong>{formatPrice(total)} &#8381;</strong>
            </div>
            <div className="app-price-checkout__actions">
              <Button
                variant="contained"
                className="app-btn app-btn--primary app-price-checkout__pay"
                onClick={onPay}
                disabled={loading || syncing}
              >
                {loading ? "Создаём платёж…" : "Перейти к оплате"}
              </Button>
              {paymentPending ? (
                <Button
                  variant="outlined"
                  className="app-btn app-price-checkout__sync"
                  onClick={onSyncPayment}
                  disabled={loading || syncing}
                >
                  {syncing ? "Проверяем…" : "Проверить оплату"}
                </Button>
              ) : null}
            </div>
          </div>
        </>
      ) : (
        <p className="app-price-checkout__empty">
          Выберите базовую подписку — итоговая сумма и кнопка оплаты появятся здесь.
          Дополнительное место необязательно.
        </p>
      )}
    </section>
  );
}

export default function Price() {
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.user?.data);
  const profile = useSelector((state) => state.user?.profile);
  const userId = getAuthUserIdFromState(userData);
  const [faqOpen, setFaqOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [paymentPending, setPaymentPending] = useState(false);
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState(null);
  const [selectedMediaId, setSelectedMediaId] = useState(null);

  const businessSubscription =
    profile?.businessSubscription ?? userData?.businessSubscription ?? null;
  const businessUntil = formatSubscriptionExpiry(businessSubscription);
  const businessPlan = formatBusinessPlanLabel(businessSubscription);
  const subscriptionActive = Boolean(businessUntil);
  const isTrial = isTrialSubscription(businessSubscription);
  const trialStatus = getTrialStatusText(businessSubscription);
  const storageUsage = profile?.storage ?? null;
  const storageLabel = formatStorageStatusLabel(storageUsage);
  const storagePercent = getStorageUsagePercent(storageUsage);

  const selectedSubscriptionPlan = useMemo(
    () =>
      PRICING_SECTION.plans.find(
        (entry) => entry.packageId === selectedSubscriptionId,
      ) ?? null,
    [selectedSubscriptionId],
  );

  const cartItems = useMemo(() => {
    const items = [];

    if (selectedSubscriptionPlan) {
      items.push({
        packageId: selectedSubscriptionPlan.packageId,
        title: selectedSubscriptionPlan.title,
        price: selectedSubscriptionPlan.price,
      });
    }

    if (selectedMediaId) {
      const plan = MEDIA_STORAGE_SECTION.plans.find(
        (entry) => entry.packageId === selectedMediaId,
      );
      if (plan) {
        items.push({
          packageId: plan.packageId,
          title: plan.title,
          price: plan.price,
        });
      }
    }

    return items;
  }, [selectedSubscriptionPlan, selectedMediaId]);

  const cartTotal = cartItems.reduce((sum, item) => sum + item.price, 0);
  const subscriptionChosen = Boolean(selectedSubscriptionPlan);

  const handleSubscriptionSelect = (packageId) => {
    setError(null);
    setInfo(null);
    setSelectedSubscriptionId(packageId);
  };

  const handleMediaSelect = (packageId) => {
    setError(null);
    setInfo(null);
    setSelectedMediaId((current) => (current === packageId ? null : packageId));
  };

  const handleSyncPayment = async () => {
    if (!userId) return;

    setError(null);
    setInfo(null);
    setSyncing(true);

    try {
      const response = await fetch(`${url}/payment/sync`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: setAuthToken(),
        },
        body: JSON.stringify({}),
      });

      const result = await readApiJson(response);

      if (result.synced) {
        setPaymentPending(false);
        setInfo(result.message || "Оплата подтверждена, подписка активирована");
        await dispatch(getProfile({ id: userId }));
        return;
      }

      setInfo(
        result.message ||
          "Платёж ещё обрабатывается. Подождите минуту и попробуйте снова.",
      );
    } catch {
      setError("Не удалось проверить оплату. Попробуйте позже.");
    } finally {
      setSyncing(false);
    }
  };

  const handlePayment = async () => {
    if (!selectedSubscriptionPlan) {
      setError("Сначала выберите базовую подписку");
      return;
    }

    if (!userId) {
      setError("Не удалось определить аккаунт. Перезайдите в систему.");
      return;
    }

    const checkoutPackageIds = selectedMediaId
      ? [selectedSubscriptionPlan.packageId, selectedMediaId]
      : [selectedSubscriptionPlan.packageId];

    setError(null);
    setInfo(null);
    setLoading(true);

    try {
      const response = await fetch(`${url}/payment/create-payment`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: setAuthToken(),
        },
        body: JSON.stringify({
          id: userId,
          packageId: selectedSubscriptionPlan.packageId,
          packageIds: checkoutPackageIds,
        }),
      });

      const raw = await response.text();
      let result = {};
      try {
        result = raw ? JSON.parse(raw) : {};
      } catch {
        result = { message: raw || "Не удалось создать платёж" };
      }

      if (!response.ok || result.type === "error") {
        setError(result.message || "Не удалось создать платёж");
        return;
      }

      const paymentUrl = result.confirmation?.confirmation_url;
      if (paymentUrl) {
        setPaymentPending(true);
        setInfo(
          "Откроется страница ЮMoney. После оплаты вернитесь сюда и нажмите «Проверить оплату».",
        );
        const opened = window.open(paymentUrl, "_blank");
        if (!opened) {
          window.location.assign(paymentUrl);
        }
        return;
      }

      setError("Платёж создан, но ссылка на оплату не получена. Попробуйте позже.");
    } catch {
      setError("Не удалось создать платёж. Попробуйте позже.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppPageShell
      className="app-page--pricing"
      title="Тарифы"
    >
      <div className="app-price-page">
        <div className="app-price-status">
          <div
            className={`app-price-status__item app-price-status__item--subscription${
              subscriptionActive
                ? isTrial
                  ? " app-price-status__item--trial"
                  : " app-price-status__item--active"
                : " app-price-status__item--inactive"
            }`}
          >
            <span className="app-price-status__label">
              <span className="app-price-status__dot" aria-hidden="true" />
              {isTrial ? "Триал" : "Подписка"}
            </span>
            <span className="app-price-status__value">
              {subscriptionActive
                ? trialStatus ?? `${businessPlan ?? "Активна"} до ${businessUntil}`
                : "Не подключена"}
            </span>
          </div>
          <div className="app-price-status__item app-price-status__item--storage">
            <span className="app-price-status__label">Место для видео</span>
            <span className="app-price-status__value">{storageLabel}</span>
            {storageUsage?.usageLabel ? (
              <div
                className="app-price-status__meter"
                role="progressbar"
                aria-valuenow={storagePercent}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`Занято ${storageUsage.usageLabel}`}
              >
                <span
                  className={`app-price-status__meter-fill${
                    storagePercent >= 90 ? " app-price-status__meter-fill--warn" : ""
                  }`}
                  style={{ width: `${storagePercent}%` }}
                />
              </div>
            ) : null}
          </div>
        </div>

        <section className="app-price-step-block">
          <div className="app-price-step">
            <h2 className="app-price-step__title">{PRICING_SECTION.title}</h2>
          </div>

          <PricingTierComparisonTable
            columns={PRICING_TIER_COMPARISON.columns}
            rows={PRICING_TIER_COMPARISON.rows}
            plans={PRICING_SECTION.plans}
            selectedPackageId={selectedSubscriptionId}
            onSelectPlan={handleSubscriptionSelect}
          />
        </section>

        <section
          className={`app-price-step-block app-price-step-block--addons${
            subscriptionChosen ? " app-price-step-block--focused" : ""
          }`}
        >
          <div className="app-price-step">
            <h2 className="app-price-step__title">{MEDIA_STORAGE_SECTION.title}</h2>
            <p className="app-price-step__desc">{MEDIA_STORAGE_SECTION.subtitle}</p>
          </div>

          {error && !subscriptionChosen ? (
            <div className="app-price-error">{error}</div>
          ) : null}

          <div className="app-price-media-options" role="group" aria-label="Дополнительное место">
            {MEDIA_STORAGE_SECTION.plans.map((plan) => {
              const selected = selectedMediaId === plan.packageId;

              return (
                <button
                  key={plan.packageId}
                  type="button"
                  className={`app-price-media-option${
                    selected ? " app-price-media-option--selected" : ""
                  }${plan.highlight ? " app-price-media-option--featured" : ""}`}
                  onClick={() => handleMediaSelect(plan.packageId)}
                  aria-pressed={selected}
                >
                  <span className="app-price-media-option__volume">{plan.title}</span>
                  <span className="app-price-media-option__price">
                    {formatPrice(plan.price)} &#8381;
                  </span>
                  <span className="app-price-media-option__period">{plan.pricePeriod}</span>
                </button>
              );
            })}
          </div>
        </section>

        <PriceCheckoutSummary
          items={cartItems}
          total={cartTotal}
          loading={loading}
          syncing={syncing}
          canPay={subscriptionChosen}
          error={error}
          info={info}
          paymentPending={paymentPending}
          onPay={handlePayment}
          onSyncPayment={handleSyncPayment}
        />

        <div className="app-price-help-row">
          <span className="app-price-help-row__text">Нужна помощь с выбором тарифа?</span>
          <button
            type="button"
            className={`app-price-help-btn${
              faqOpen ? " app-price-help-btn--active" : ""
            }`}
            onClick={() => setFaqOpen((open) => !open)}
            aria-expanded={faqOpen}
            aria-label="Частые вопросы"
            title="Частые вопросы"
          >
            <HelpOutlineIcon fontSize="small" />
          </button>
        </div>

        {faqOpen ? (
          <section className="app-price-faq app-price-faq--inline">
            <h2 className="app-price-faq__title">Частые вопросы</h2>
            <div className="app-price-faq__list">
              {PRICING_FAQ.map((item) => (
                <details className="app-price-faq__item" key={item.question}>
                  <summary>{item.question}</summary>
                  <p>{item.answer}</p>
                </details>
              ))}
            </div>
          </section>
        ) : null}

        <footer className="app-price-footnote">
          <p>
            Оплата через{" "}
            <a href="https://yoomoney.ru" target="_blank" rel="noreferrer">
              ЮMoney
            </a>
            . Базовая подписка и медиа-пакеты — на 30 дней, продление вручную в этом разделе.
          </p>
          <p>Поддержка: support@club-lab.ru</p>
        </footer>
      </div>
    </AppPageShell>
  );
}
